/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useState } from "react";

import { CloudUpload } from "icons";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";
import { H6R } from "typography/captions";
import { H4SB2 } from "typography/headers";
import { H4R2 } from "typography/headers";
import { v4 as uuidv4 } from "uuid";

const defaultMaxFileSize = 10 * 1000 * 1000 * 10; //100MB
const defaultFileTypes = [
  ".csv",
  ".doc",
  ".docx",
  ".eml",
  ".gif",
  ".jpg",
  ".jpeg",
  ".msg",
  ".pdf",
  ".png",
  ".txt",
  ".xls",
  ".xlsx",
];

type MultiFileSelectProps = {
  onChange?: (file: File[]) => void;
  disabled?: boolean;
  acceptedFileTypes?: string[];
  maxSize?: number;
  className?: string;
};
const MultiFileSelect: React.FC<MultiFileSelectProps> = ({
  onChange,
  disabled,
  maxSize = defaultMaxFileSize,
  acceptedFileTypes = defaultFileTypes,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const id = "file-upload--" + uuidv4();

  const handleFilesDropped = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files) {
      handleFileChanged(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileChanged = (files: File[]) => {
    if (!disabled) {
      setErrors([]);
      const fileErrors: React.SetStateAction<string[]> = [];
      files.map((file) => {
        const fileParts = file.name.split(".");
        const fileExtension = fileParts[fileParts.length - 1] ?? "";
        if (file.size > maxSize) {
          fileErrors.push(
            `File is too large, max size is ${maxSize / 1000 / 1000}MB`
          );
        }
        if (
          !acceptedFileTypes.find(
            (ext) => ext === `.${fileExtension.toLowerCase()}`
          )
        ) {
          fileErrors.push(
            `This file type (.${fileExtension}) is not supported. Please choose a different file.`
          );
        }
      });
      if (fileErrors.length > 0) {
        setErrors(fileErrors);
      } else {
        onChange && onChange(files);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.files) {
      handleFileChanged(Array.from(e.target.files));
      e.currentTarget.value = ""; // fix trying to upload same file multiple times
    }
  };
  return (
    <DropArea
      className={className}
      $disabled={disabled}
      onClick={() => {
        fileInputRef?.current?.click();
      }}
      onDrop={handleFilesDropped}
      onDragOver={(e) => {
        e.preventDefault();
      }}
    >
      <IconContainer $disabled={disabled}>
        <UploadIcon size={18} $disabled={disabled} />
      </IconContainer>
      <HiddenInput
        ref={fileInputRef}
        type="file"
        role="input"
        name="file-upload"
        id={id}
        disabled={disabled}
        multiple={true}
        onChange={handleFileSelect}
        accept={acceptedFileTypes.join(",")}
      />
      <CTAWrapper>
        <UploadLink $disabled={disabled}>Click to upload</UploadLink>
        <DNDInstructions>or drag and drop</DNDInstructions>
      </CTAWrapper>
      <H6R>{acceptedFileTypes.join(", ")} files</H6R>
      {errors.length > 0 && (
        <ErrorWrapper>
          {errors.map((error) => (
            <li key={error}>
              <Error>{error} </Error>
            </li>
          ))}
        </ErrorWrapper>
      )}
    </DropArea>
  );
};

const HiddenInput = styled.input`
  width: 0;
  visibility: hidden;
  height: 0;
`;

const DropArea = styled.div<{ $disabled?: boolean }>`
  ${({ $disabled }) => css`
    padding: 16px 24px;
    min-height: 124px;
    background-color: ${$disabled ? color.neutral[100] : color.neutral[100]};
    border: 1px dashed
      ${$disabled ? color.foreground.disabled : color.blue[300]};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    transition: background-color 0.5s;

    ${!$disabled &&
    css`
      cursor: pointer;

      &:hover {
        background-color: ${color.blue[100]};
      }
    `}
  `}
`;

const UploadIcon = styled(CloudUpload)<{ $disabled?: boolean }>`
  color: ${color.neutral[400]};
  transition: color 0.5s;

  ${DropArea}:hover & {
    ${({ $disabled }) =>
      !$disabled &&
      css`
        color: ${color.blue[300]};
      `}
  }
`;

const IconContainer = styled.div<{ $disabled?: boolean }>`
  ${({ $disabled }) => css`
    min-width: 40px;
    min-height: 40px;
    background-color: ${color.neutral[200]};
    border-radius: 28px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 0.5s;

    ${DropArea}:hover & {
      ${!$disabled && `background-color: ${color.blue[100]}`};
    }
  `}
`;

const CTAWrapper = styled.div`
  margin-top: 12px;
`;

const UploadLink = styled(H4SB2)<{ $disabled?: boolean }>`
  ${({ $disabled }) => css`
    color: ${$disabled ? color.foreground.disabled : color.blue[300]};
    display: inline;
  `}
`;

const DNDInstructions = styled(H4R2)`
  display: inline;
  color: ${color.neutral[400]};
  margin-left: 4px;
`;

const ErrorWrapper = styled.ul`
  list-style: none;
  padding: 0;
  margin: 16px 0 0 0;
`;

const Error = styled(H6R)`
  color: ${color.red[300]};
`;

export default MultiFileSelect;
